import React, { useEffect, useRef, useState } from "react";
import { MessageSquareText, X, Send, Phone, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { attribution, track } from "@/lib/analytics";
import { toast } from "sonner";

const GREETING = "Ciao! 👋 Sono l'assistente SENIORCAR. Posso aiutarti a capire se SENIORCAR fa per te o per un tuo familiare, e rispondere a domande su prezzo, patente, agevolazioni e ordine online. Come posso aiutarti?";

function getSession() {
  let s = localStorage.getItem("sc_chat_session");
  if (!s) { s = (crypto.randomUUID ? crypto.randomUUID() : String(Date.now())); localStorage.setItem("sc_chat_session", s); }
  return s;
}

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETING }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [showLead, setShowLead] = useState(false);
  const [lead, setLead] = useState({ first_name: "", phone: "", province: "" });
  const [leadSent, setLeadSent] = useState(false);
  const session = useRef(getSession());
  const scrollRef = useRef(null);

  useEffect(() => { if (open) track("chatbot_open", {}); }, [open]);

  useEffect(() => { scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" }); }, [messages, loading, open]);

  const toggle = () => setOpen((o) => !o);

  const send = async (e) => {
    e?.preventDefault();
    const text = input.trim();
    if (!text || loading) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setLoading(true);
    try {
      const { data } = await api.post("/assistant/chat", { session_id: session.current, message: text });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", text: "Scusa, al momento non riesco a rispondere. Puoi lasciarmi il tuo contatto qui sotto o chiamarci: ti aiutiamo subito." }]);
      setShowLead(true);
    } finally { setLoading(false); }
  };

  const submitLead = async (e) => {
    e.preventDefault();
    if (!lead.first_name || !lead.phone) return toast.error("Inserisci nome e telefono.");
    try {
      await api.post("/leads", { ...lead, lead_type: "chatbot", requested_action: "qualified", privacy_consent: true, notes: "Lead da assistente AI", ...attribution() });
      track("qualified_lead", { form: "chatbot" });
      setLeadSent(true);
      setMessages((m) => [...m, { role: "assistant", text: `Grazie ${lead.first_name}! Ti ricontatteremo al più presto. 😊` }]);
    } catch { toast.error("Errore nell'invio. Riprova."); }
  };

  return (
    <>
      <button onClick={toggle}
        className="fixed z-40 flex items-center justify-center rounded-full shadow-lg text-white transition-transform hover:scale-105 bottom-[92px] right-4 lg:bottom-6 lg:right-6 w-14 h-14"
        style={{ backgroundColor: "var(--sc-emerald)" }} data-testid="chat-fab" aria-label="Apri assistente">
        {open ? <X size={26} /> : <MessageSquareText size={26} />}
      </button>

      {open && (
        <div className="fixed z-40 bg-white rounded-2xl shadow-2xl border border-slate-200 flex flex-col overflow-hidden
          bottom-[160px] right-4 left-4 lg:left-auto lg:right-6 lg:bottom-24 w-auto lg:w-[380px] h-[70vh] max-h-[560px]" data-testid="chat-panel">
          <div className="px-4 py-3 text-white flex items-center gap-2" style={{ backgroundColor: "var(--sc-emerald)" }}>
            <MessageSquareText size={20} />
            <div><p className="font-bold leading-tight" style={{ fontFamily: "Outfit" }}>Assistente SENIORCAR</p><p className="text-white/70 text-xs">Ti risponde in pochi secondi</p></div>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-[var(--sc-canvas)]">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${m.role === "user" ? "text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-700 rounded-bl-sm"}`}
                  style={m.role === "user" ? { backgroundColor: "var(--sc-amber)" } : {}} data-testid={`chat-msg-${m.role}`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && <div className="flex justify-start"><div className="bg-white border border-slate-200 rounded-2xl rounded-bl-sm px-4 py-3 text-slate-400 text-sm">L'assistente sta scrivendo…</div></div>}

            {showLead && !leadSent && (
              <form onSubmit={submitLead} className="bg-white border border-emerald-200 rounded-2xl p-4 space-y-2" data-testid="chat-lead-form">
                <p className="font-semibold text-slate-800 text-sm">Lasciaci un contatto, ti richiamiamo noi:</p>
                <Input placeholder="Nome" value={lead.first_name} onChange={(e) => setLead({ ...lead, first_name: e.target.value })} className="h-10" data-testid="chat-lead-name" />
                <Input placeholder="Telefono" value={lead.phone} onChange={(e) => setLead({ ...lead, phone: e.target.value })} className="h-10" data-testid="chat-lead-phone" />
                <Input placeholder="Provincia (es. MI)" value={lead.province} onChange={(e) => setLead({ ...lead, province: e.target.value })} className="h-10" data-testid="chat-lead-province" />
                <Button type="submit" className="w-full h-10 font-bold text-white rounded-full" style={{ backgroundColor: "var(--sc-amber)" }} data-testid="chat-lead-submit">Richiamatemi</Button>
              </form>
            )}
            {leadSent && <div className="flex items-center gap-2 text-emerald-800 text-sm font-semibold"><CheckCircle2 size={18} /> Contatto inviato!</div>}
          </div>

          <div className="border-t border-slate-200 p-2.5 bg-white">
            {!showLead && !leadSent && (
              <button onClick={() => setShowLead(true)} className="w-full mb-2 text-xs font-semibold text-emerald-800 flex items-center justify-center gap-1 hover:underline" data-testid="chat-request-callback">
                <Phone size={13} /> Fatti richiamare da un consulente
              </button>
            )}
            <form onSubmit={send} className="flex gap-2">
              <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Scrivi la tua domanda…" className="h-11" data-testid="chat-input" />
              <Button type="submit" disabled={loading} className="h-11 w-11 p-0 shrink-0 text-white rounded-full" style={{ backgroundColor: "var(--sc-emerald)" }} data-testid="chat-send"><Send size={18} /></Button>
            </form>
          </div>
        </div>
      )}
    </>
  );
};
